import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Col, Container, Form, Modal, Spinner, Table } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useConfirm from '../../../UI/organisms/ConfirmDialog/use-confirm';
import Card from '../../../UI/molecules/Card/card';
import { selectCurentUserRoles } from '../../../../store/slices/auth-slice';
import {
  copyLogicDefinitionForward, getLogicDefinitionHistory, getLogicDefinitions, getNamedValues, NamedValueDeclaration,
  retireLogicDefinition,
} from '../../../../services/requests/pricing/logic-definitions-requests';
import {
  EditorKind, hasPricingEditRole, LIQUID_CUSTOMER_OWNER, summarize,
} from './conditional-charge-expressions';
import { ConditionalChargeEditor, LogicDefinition } from './conditional-charge-editor';

const formatDate = (value?: string | null) => (value ? moment(value).format('MM/DD/YYYY') : '');
const formatDateTime = (value?: string | null) => (value ? moment.utc(value).local().format('MM/DD/YYYY h:mm A') : '');

const STATUS_VARIANT: Record<string, string> = { Active: 'success', Superseded: 'secondary', Retired: 'dark' };

interface EditorState { kind: EditorKind | null; definition: LogicDefinition | null; readOnly: boolean }

/** Version history of one definition; each version can be opened read-only. */
const HistoryModal = ({ lineageId, onClose, onView }: { lineageId: string; onClose: () => void; onView: (d: LogicDefinition) => void }) => {
  const [versions, setVersions] = useState<LogicDefinition[] | null>(null);

  useEffect(() => {
    let active = true;
    getLogicDefinitionHistory(lineageId)
      .then(data => active && setVersions(data ?? []))
      .catch(() => active && setVersions([]));
    return () => {
      active = false;
    };
  }, [lineageId]);

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>History</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!versions ? <Spinner animation="border" size="sm" /> : (
          <Table size="sm" striped bordered className="mb-0">
            <thead>
              <tr><th>Version</th><th>Name</th><th>Effective</th><th>Status</th><th>Saved by</th><th>Saved</th><th /></tr>
            </thead>
            <tbody>
              {versions.map(v => (
                <tr key={v.id}>
                  <td>{v.version}</td>
                  <td>{v.name}</td>
                  <td className="text-nowrap">{formatDate(v.effectiveFrom)} – {formatDate(v.effectiveTo)}</td>
                  <td><Badge bg={STATUS_VARIANT[v.status]}>{v.status}</Badge></td>
                  <td>{v.createdBy}</td>
                  <td className="text-nowrap">{formatDateTime(v.createdAt)}</td>
                  <td><Button size="sm" variant="link" className="p-0" onClick={() => onView(v)}>View</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

/**
 * The Conditional Charges card on the liquid customer Pricing tab. Renders nothing (no card, no requests) unless the
 * user explicitly holds the Pricing Edit role; Application Admin alone does not reveal it while the feature is built.
 */
export const ConditionalChargesPanel = (props: any) => {
  const roles = useSelector(selectCurentUserRoles) as { roleId: number }[] | undefined;
  if (!hasPricingEditRole(roles)) return null;
  return (
    <Col>
      <Card header="Conditional Charges" className="card-secondary card-outline h-100">
        <Container fluid className="p-0 m-0">
          <ConditionalChargesSection {...props} />
        </Container>
      </Card>
    </Col>
  );
};

/**
 * Conditional charges (purge fee ranges, depressurization fee matrix) and named values for a liquid customer.
 * Definitions are stored by the Pricing domain and saved independently of the customer form; saving is immediate.
 * Only rendered for Pricing Edit holders (see ConditionalChargesPanel), so everyone who sees it can edit.
 */
export const ConditionalChargesSection = (props: any) => {
  const customerId = props.entity?.customerId;
  const { confirm: confirmHook } = useConfirm();
  const confirm = props.confirm ?? confirmHook;

  const [items, setItems] = useState<LogicDefinition[]>([]);
  const [namedValues, setNamedValues] = useState<NamedValueDeclaration[]>([]);
  const [loading, setLoading] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const data = await getLogicDefinitions({
        ownerType: LIQUID_CUSTOMER_OWNER, ownerId: String(customerId), includeRetired: showRetired,
      });
      setItems(data ?? []);
    } catch {
      toast.error('Failed to load conditional charges.');
    } finally {
      setLoading(false);
    }
  }, [customerId, showRetired]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getNamedValues().then(data => setNamedValues(data ?? [])).catch(() => setNamedValues([]));
  }, []);

  const open = (state: EditorState) => {
    setEditorKey(k => k + 1);
    setEditor(state);
  };

  const copyForward = async (d: LogicDefinition) => {
    const from = moment(d.effectiveFrom).add(1, 'year');
    const to = moment(d.effectiveTo).add(1, 'year');
    if (!await confirm(`Copy "${d.name}" to ${from.format('MM/DD/YYYY')} – ${to.format('MM/DD/YYYY')}?`)) return;
    try {
      const copy = await copyLogicDefinitionForward(d.id);
      toast.success(`Created "${copy.name}".`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const retire = async (d: LogicDefinition) => {
    if (!await confirm(`Retire "${d.name}"? It will no longer apply. Its history is kept.`)) return;
    try {
      await retireLogicDefinition(d.id);
      toast.success(`Retired "${d.name}".`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  if (!customerId) return <p className="text-muted mb-0">Save the customer before adding conditional charges.</p>;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Button size="sm" onClick={() => open({ kind: 'purge', definition: null, readOnly: false })}>+ Purge Fee</Button>
        <Button size="sm" onClick={() => open({ kind: 'matrix', definition: null, readOnly: false })}>+ Depressurization Fee</Button>
        <Button size="sm" variant="outline-primary" onClick={() => open({ kind: 'value', definition: null, readOnly: false })}>+ Named Value</Button>
        <Form.Check
          type="switch"
          id="conditional-charges-show-retired"
          className="ms-auto"
          label="Show retired"
          checked={showRetired}
          onChange={e => setShowRetired(e.target.checked)}
        />
        {loading && <Spinner animation="border" size="sm" />}
      </div>
      <small className="text-muted d-block mb-2">Saved separately from the customer; changes take effect as soon as they are saved.</small>
      <Table size="sm" bordered hover className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>Name</th><th>Type</th><th>Effective</th><th>Layout</th><th>Version</th><th>Status</th><th>Saved</th><th />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={8} className="text-muted">There are no conditional charges for this customer.</td></tr>
          )}
          {items.map(d => {
            const { type, summary } = summarize(d.expression, d.editorHint);
            const active = d.status === 'Active';
            const editable = active && type !== 'Custom';
            return (
              <tr key={d.id}>
                <td>{d.name}</td>
                <td>{type}</td>
                <td className="text-nowrap">{formatDate(d.effectiveFrom)} – {formatDate(d.effectiveTo)}</td>
                <td>{summary}</td>
                <td>{d.version}</td>
                <td><Badge bg={STATUS_VARIANT[d.status]}>{d.status}</Badge></td>
                <td className="text-nowrap">{d.createdBy}<br /><small className="text-muted">{formatDateTime(d.createdAt)}</small></td>
                <td className="text-nowrap">
                  <Button size="sm" variant="link" className="p-0 me-2" onClick={() => open({ kind: null, definition: d, readOnly: !editable })}>
                    {editable ? 'Edit' : 'View'}
                  </Button>
                  <Button size="sm" variant="link" className="p-0 me-2" onClick={() => setHistoryFor(d.lineageId)}>History</Button>
                  <Button size="sm" variant="link" className="p-0 me-2" onClick={() => copyForward(d)}>Copy forward</Button>
                  {active && (
                    <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => retire(d)}>Retire</Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {editor && (
        <ConditionalChargeEditor
          key={editorKey}
          show
          kind={editor.kind}
          definition={editor.definition}
          readOnly={editor.readOnly}
          customerId={customerId}
          namedValues={namedValues}
          onNamedValueCreated={created => setNamedValues(current => [...current, created].sort((a, b) => a.name.localeCompare(b.name)))}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}
      {historyFor && (
        <HistoryModal
          lineageId={historyFor}
          onClose={() => setHistoryFor(null)}
          onView={d => {
            setHistoryFor(null);
            open({ kind: null, definition: d, readOnly: true });
          }}
        />
      )}
    </div>
  );
};
